import { useState, useMemo, useCallback, type ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { isTetSubTeam } from '@cortex/domain-product-innovation-lab-types';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { useMemberGithubIndex, useMemberList } from '../../hooks/useMember.js';
import { useCombatPower } from '../../hooks/useCombatPower.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton/index.js';
import { MembersCombatPowerSection } from '../../components/features/combat-power/index.js';
import { detectCurrentMonthIdx } from '../../components/features/combat-power/combat-power-utils.js';
import type { QuarterSummaryScope } from '../../components/features/combat-power/index.js';
import { MemberHealthDiagnosisSection } from '../../components/features/health/index.js';
import { ProtectedRoute } from '../../components/auth/index.js';
import { formatPt } from '../../lib/format.js';
import { TEAM_OPTIONS } from '@cortex/domain-product-innovation-lab-kpi-team-filter';
import type { MemberOrganization } from '../../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エンジニア看板ページ（旧メンバー管理ページ本体の移設先）。組織フィルタでエンジニア・QAメンバーの戦闘力サマリ / GitHub活動ランキング / 一覧を表示し、詳細ページへ遷移。メンバー管理フォルダ配下のサブページ
 * @graph-connects useMemberList [calls] メンバー一覧フック呼び出し
 * @graph-connects useMemberGithubIndex [calls] GitHub activity index フック呼び出し
 */
export const Route = createFileRoute('/members/engineer-board')({
  component: EngineerBoardPage,
});

/** @graph-connects ProtectedRoute [calls] engineerBoard(=旧 members) アクセス条件でアクセスをガードする */
function EngineerBoardPage() {
  return (
    <ProtectedRoute pageKey="engineerBoard">
      <EngineerBoardPageContent />
    </ProtectedRoute>
  );
}

/**
 * @graph-business 看板ページの見出し。説明文は置かず、見出しとフィルタの並びで画面を説明させる
 * @graph-connects none
 */
function BoardHeader({ filters }: { filters?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <h1 className="animate-content-reveal text-2xl font-bold text-[var(--color-text-primary)]">
        {t('members.title')}
      </h1>
      {filters !== undefined && (
        <div className="animate-content-reveal flex items-center gap-3">{filters}</div>
      )}
    </div>
  );
}

/**
 * @graph-business セクション見出しと件数。件数はラベル文に連結せず見出しの隣に数値として置く
 * @graph-connects none
 */
function SectionTitle({ label, count }: { label: string; count: number }) {
  return (
    <span className="flex items-baseline gap-2">
      {label}
      <span className="text-sm font-normal tabular-nums text-[var(--color-text-muted)]">
        {count}
      </span>
    </span>
  );
}

/**
 * @graph-business 数値を主役にした指標表示。ラベル行を上に積まず、単位として値の隣に小さく添える
 * @graph-connects none
 */
function Metric({ value, unit }: { value: string; unit: string }) {
  return (
    <p className="flex items-baseline gap-1.5">
      <span className="text-base font-semibold tabular-nums text-[var(--color-text-primary)]">
        {value}
      </span>
      <span className="text-xs text-[var(--color-text-muted)]">{unit}</span>
    </p>
  );
}

/**
 * @graph-business 行遷移を示す chevron。アイコンだけで意味が通るためラベルは持たない
 * @graph-connects none
 */
function Chevron({ className }: { className: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

/** @graph-connects pilab-api [calls] PILab APIを呼び出す */
function EngineerBoardPageContent() {
  const { t } = useTranslation();
  const [selectedOrg, setSelectedOrg] = useState<MemberOrganization | undefined>(undefined);
  const [selectedSheet, setSelectedSheet] = useState<string | undefined>();
  const [selectedMonthIdx, setSelectedMonthIdx] = useState<number | undefined>();

  // 組織オプション
  // 選択肢は 5 画面共通カタログ（TEAM_OPTIONS）を正本とする
  const orgOptions: { value: MemberOrganization | 'all'; label: string }[] = TEAM_OPTIONS.map(
    (o) => ({
      value: o.value as MemberOrganization | 'all',
      label: o.value === 'all' ? t('kpi.scope.all') : o.label,
    }),
  );

  const { data, isLoading, error, refetch } = useMemberList({
    organization: selectedOrg,
  });
  const { data: combatData } = useCombatPower(selectedSheet);
  const { data: githubIndex } = useMemberGithubIndex({ range: '30d', sort: 'lastActivityAt' });

  // Resolve effective month index: user selection or auto-detect from current date
  const currentMonthIdx = useMemo(() => {
    if (!combatData) return 0;
    if (selectedMonthIdx !== undefined && selectedMonthIdx < combatData.monthHeaders.length) {
      return selectedMonthIdx;
    }
    return detectCurrentMonthIdx(combatData.monthHeaders);
  }, [combatData, selectedMonthIdx]);

  // When sheet changes, reset month selection so it auto-detects for the new quarter
  const handleSheetChange = useCallback((sheet: string) => {
    setSelectedSheet(sheet);
    setSelectedMonthIdx(undefined);
  }, []);

  const engineers = data?.engineers ?? [];
  const qaMembers = data?.qa ?? [];

  // Q(四半期) 切替オプション。過去 Q を含む全 Q シートから選択できる。
  // CombatPowerBlock 側で追加した未来 Q (quarterSheets 未収載) も選択状態を保てるよう補完する
  const sheetOptions = useMemo(() => {
    if (!combatData) return [];
    const options = combatData.quarterSheets.map((s) => ({ value: s.title, label: s.title }));
    if (!options.some((o) => o.value === combatData.selectedSheet)) {
      options.unshift({ value: combatData.selectedSheet, label: combatData.selectedSheet });
    }
    return options;
  }, [combatData]);

  // Qサマリーカードの集計スコープ。全体=PDG+ACE 合算、PDG / PDG TE / TE サブチームビュー=PDG、
  // ACE ビュー=ACE、個別 ACE チームビュー=そのチーム。全フィルタで Q の達成度を確認できるようにする。
  // TE サブチームは Q 予算を独立に持たない（予算は TE まで）ため、親の PDG に丸める。
  const quarterSummaryScope: QuarterSummaryScope =
    !selectedOrg || selectedOrg === 'all'
      ? 'overall'
      : selectedOrg === 'tet' || selectedOrg === 'pdg' || isTetSubTeam(selectedOrg)
        ? 'pdg'
        : selectedOrg;

  // 戦闘力テーブルの担当者名 (nickName) からメンバー詳細ページの email を引くための対応表。
  // 戦闘力データ側は小文字 nickName で持つことがあるためキーを小文字で正規化する。
  const nameToEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of [...engineers, ...qaMembers]) {
      if (m.nickName) map.set(m.nickName.toLowerCase(), m.email);
    }
    return map;
  }, [engineers, qaMembers]);

  // チームマッピングはunified_members VIEW由来（combatData.memberTeamMap）
  const memberTeamMap = useMemo(() => {
    const map = new Map<string, { group: string; team: string }>();
    if (combatData?.memberTeamMap) {
      for (const m of combatData.memberTeamMap) {
        map.set(m.name, { group: m.group, team: m.team });
      }
    }
    return map;
  }, [combatData?.memberTeamMap]);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <BoardHeader />
        <KpiTableSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <BoardHeader />
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header: 見出し + Quarter (Qシート) / 組織フィルタ。説明文は置かない */}
      <BoardHeader
        filters={
          <>
            {combatData && (
              <Select
                options={sheetOptions}
                value={combatData.selectedSheet}
                onChange={(e) => {
                  handleSheetChange(e.target.value);
                }}
                size="sm"
                aria-label={t('members.combatPower.quarterSelectLabel')}
              />
            )}
            <Select
              options={orgOptions}
              value={selectedOrg ?? 'all'}
              onChange={(e) => {
                setSelectedOrg(
                  e.target.value === 'all' ? undefined : (e.target.value as MemberOrganization),
                );
              }}
              size="sm"
            />
          </>
        }
      />

      {/* 戦闘力サマリー (スコープ別: 全体 / PDG TE / ACE)。Qサマリー(実績 vs 目標)を含む */}
      {combatData && (
        <MembersCombatPowerSection
          data={combatData}
          selectedOrg={selectedOrg}
          quarterSummaryScope={quarterSummaryScope}
          memberTeamMap={memberTeamMap}
          currentMonthIdx={currentMonthIdx}
          onMonthChange={setSelectedMonthIdx}
          onSheetChange={handleSheetChange}
          nameToEmail={nameToEmail}
        />
      )}

      {/* 健康度診断（ACE / PDG TE / ACE 各チームビューのみ）。担当中PJの平均健康度を team 別に一覧 */}
      <MemberHealthDiagnosisSection
        selectedOrg={selectedOrg}
        engineers={engineers}
        qaMembers={qaMembers}
      />

      {/* Engineers Table */}
      <Card className="animate-content-reveal">
        <CardHeader title={t('members.githubActivity.ranking.title')} />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('members.githubActivity.table.member')}
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('members.githubActivity.table.pr')}
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('members.githubActivity.table.review')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                    {t('members.githubActivity.table.last')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(githubIndex?.data ?? []).slice(0, 10).map((row) => (
                  <tr
                    key={row.member.githubLogin}
                    className="border-b border-[var(--color-border)] last:border-b-0"
                  >
                    <td className="py-3 px-4 text-sm text-[var(--color-text-primary)]">
                      <div className="font-medium">{row.member.displayName}</div>
                      <div className="text-xs text-[var(--color-text-muted)]">
                        {row.member.githubLogin}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-right">{row.prOpened}</td>
                    <td className="py-3 px-4 text-sm text-right">{row.reviewsSubmitted}</td>
                    <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                      {row.lastActivityAt?.slice(0, 10) ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Engineers Table */}
      <Card className="animate-content-reveal">
        <CardHeader
          title={<SectionTitle label={t('members.engineers')} count={engineers.length} />}
        />
        <CardContent>
          {engineers.length === 0 ? (
            <div className="text-center py-8 text-[var(--color-text-muted)]">
              {t('members.noMembers')}
            </div>
          ) : (
            <>
              {/* Mobile: Card view */}
              <div className="block lg:hidden space-y-3">
                {engineers.map((member) => (
                  <Link
                    key={member.email}
                    to="/members/$email"
                    params={{ email: member.email }}
                    className="block p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] hover:border-[var(--color-border-hover)] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--color-text-primary)] truncate">
                          {member.nickName ?? member.name}
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)] truncate">
                          {member.email}
                        </p>
                        {/* グループ / チームは階層なので区切り 1 つで見せ、ラベルは置かない */}
                        <p className="mt-0.5 text-xs text-[var(--color-text-muted)] truncate">
                          {member.groupName} · {member.teamName}
                        </p>
                      </div>
                      <Chevron className="mt-1 w-4 h-4 shrink-0 text-[var(--color-text-muted)]" />
                    </div>
                    <div className="mt-3 flex items-baseline gap-6">
                      <Metric
                        value={formatPt(member.points30d)}
                        unit={t('members.fields.points30d')}
                      />
                      <Metric
                        value={String(member.releaseCount30d)}
                        unit={t('members.fields.releaseCount30d')}
                      />
                    </div>
                  </Link>
                ))}
              </div>

              {/* Desktop: Table view */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.nickName')}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.group')}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.team')}
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.points30d')}
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.releaseCount30d')}
                      </th>
                      {/* registry 例外: `docs/guidelines/frontend.md` の「ニックネーム優先」原則は守りつつ、
                          メンバー管理表は本名 / email / github を 1 つのレジストリとして並べる用途のため
                          本名カラムを意図的に残している。他画面では本名を出さないこと。 */}
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.name')}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.email')}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.githubName')}
                      </th>
                      <th className="w-12 py-3 px-4">
                        <span className="sr-only">{t('actions.viewDetails')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {engineers.map((member) => (
                      <tr
                        key={member.email}
                        className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                      >
                        <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] font-medium">
                          {member.nickName ?? '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                          {member.groupName}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                          {member.teamName}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right font-medium">
                          {formatPt(member.points30d)}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right font-medium">
                          {member.releaseCount30d}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                          {member.name}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                          {member.email}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                          {member.githubName ?? '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Link
                            to="/members/$email"
                            params={{ email: member.email }}
                            className="inline-flex items-center justify-center p-2 rounded-lg text-[var(--color-accent-primary)] hover:bg-[var(--color-accent-muted)] transition-colors"
                            aria-label={t('actions.viewDetails')}
                          >
                            <Chevron className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* QA Table */}
      <Card className="animate-content-reveal">
        <CardHeader title={<SectionTitle label={t('members.qa')} count={qaMembers.length} />} />
        <CardContent>
          {qaMembers.length === 0 ? (
            <div className="text-center py-8 text-[var(--color-text-muted)]">
              {t('members.noMembers')}
            </div>
          ) : (
            <>
              {/* Mobile: Card view */}
              <div className="block md:hidden space-y-3">
                {qaMembers.map((member) => (
                  <div
                    key={member.email}
                    className="p-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
                  >
                    <p className="font-medium text-[var(--color-text-primary)] truncate">
                      {member.nickName ?? member.name}
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)] truncate">
                      {member.email}
                    </p>
                    <div className="mt-3">
                      <Metric
                        value={formatPt(member.points30d)}
                        unit={t('members.fields.outputPoints30d')}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table view */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.nickName')}
                      </th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.outputPoints30d')}
                      </th>
                      {/* registry 例外: メンバー管理表は本名 / email を 1 つのレジストリとして並べる用途のため
                          本名カラムを意図的に残している。他画面ではニックネーム優先で本名を出さないこと。 */}
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.name')}
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">
                        {t('members.fields.email')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {qaMembers.map((member) => (
                      <tr
                        key={member.email}
                        className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                      >
                        <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] font-medium">
                          {member.nickName ?? '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-primary)] text-right font-medium">
                          {formatPt(member.points30d)}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                          {member.name}
                        </td>
                        <td className="py-3 px-4 text-sm text-[var(--color-text-secondary)]">
                          {member.email}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
